import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import Domain, { DomainStrings } from 'enums/Domain'
import { MessageConst } from 'enums'
import { useDisplayTheme } from 'hooks/useUiState'
import StreamSymbol, { StreamSymbolStrings } from 'enums/StreamSymbol'
import { simpleNumberFormatter, compactNumberFormatter } from 'utils/numberFormatterUtil'
import { AUTO_ATTR_KEY, AutomationId } from 'utils/automationUtil'
import { useContextMediaDetail } from './context'

const Container = styled.div.attrs({ [AUTO_ATTR_KEY]: AutomationId.popularMedia.metrics })`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
`

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Name = styled.div<{ mode: string }>`
  color: ${({ mode }) => sys[mode].text.placeholder};
  font-size: ${sys.typescale[12].size};
  line-height: ${sys.typescale[12].lineHeight};
  font-weight: ${sys.fontWeight.regular};
`

const Value = styled.div<{ mode: string }>`
  color: ${({ mode }) => sys[mode].text.weak};
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
  font-weight: ${sys.fontWeight.medium};
`

const formatMetricValue = ({ value, isPodcast }: { value?: number | null; isPodcast: boolean }) => {
  if (typeof value !== 'number') {
    return MessageConst.EM_DASH
  }
  return isPodcast
    ? compactNumberFormatter({ value, precise: false })
    : simpleNumberFormatter(value)
}

type MetricIds =
  | 'retweets'
  | 'likes'
  | 'replies'
  | 'shares'
  | 'comments'
  | 'views'
  | 'reblogs'
  | 'listens'
type MetricToDataKey = Partial<Record<MetricIds, MetricSeriesStrings>>
type DomainToMetricToDataKey = Partial<Record<DomainStrings | StreamSymbolStrings, MetricToDataKey>>

type ChannelMetricsProps = {
  className?: string
}

const ChannelMetrics = ({ className }: ChannelMetricsProps) => {
  const displayTheme = useDisplayTheme()
  const { t } = useTranslation()
  const { domain, metricSeriesData, dataStream } = useContextMediaDetail()

  const isPodcast = dataStream === StreamSymbol.PODCAST

  const isTwitterFireHose = Domain.TWITTER && metricSeriesData?.[MetricSeries.LIKES] !== undefined
  const domainToMetricToDataKey: DomainToMetricToDataKey = {
    [Domain.TWITTER]: {
      ...(isTwitterFireHose && { views: MetricSeries.VIEWS }),
      retweets: MetricSeries.TOTAL_REPOSTS,
      ...(isTwitterFireHose && { likes: MetricSeries.LIKES }),
      replies: MetricSeries.TOTAL_REPLIES
    },
    [Domain.FACEBOOK]: {
      shares: MetricSeries.TOTAL_REPOSTS,
      likes: MetricSeries.LIKES,
      comments: MetricSeries.TOTAL_REPLIES
    },
    [Domain.YOUTUBE]: {
      views: MetricSeries.VIEWS,
      likes: MetricSeries.LIKES,
      comments: MetricSeries.TOTAL_REPLIES
    },
    [Domain.INSTAGRAM]: {
      likes: MetricSeries.LIKES,
      comments: MetricSeries.TOTAL_REPLIES
    },
    [Domain.TIKTOK]: {
      views: MetricSeries.VIEWS,
      comments: MetricSeries.TOTAL_REPLIES,
      shares: MetricSeries.TOTAL_REPOSTS,
      likes: MetricSeries.LIKES
    },
    [StreamSymbol.PODCAST]: {
      listens: MetricSeries.VIEWS
    }
  }

  const metricToLabelkey: Record<MetricIds, string> = {
    retweets: 'retweet_other',
    replies: 'reply_other',
    shares: 'share_other',
    likes: 'like_other',
    comments: 'comment_other',
    views: 'view_other',
    reblogs: 'reblog_other',
    listens: 'listen_other'
  }

  const source = isPodcast ? StreamSymbol.PODCAST : domain!
  const metricToDataKey = source ? domainToMetricToDataKey[source] : {}

  return (
    <Container className={className}>
      {metricToDataKey &&
        Object.entries(metricToDataKey).map(([metric, dataKey]) => (
          <Metric key={metric}>
            <Name mode={displayTheme}>{t(metricToLabelkey[metric as MetricIds])}</Name>
            <Value mode={displayTheme}>
              {formatMetricValue({ value: metricSeriesData?.[dataKey], isPodcast })}
            </Value>
          </Metric>
        ))}
    </Container>
  )
}

export default ChannelMetrics
